
import { EDIT_ACCOUNT, FUNCTIONS, GET_ACCOUNT } from "constants/roles";
import Main from "pages/Main";
import Layout from "pages/authentication/Layout";
import Callback from "pages/authentication/callback";
import DownloadApp from "pages/authentication/download-app/DownloadApp";
import Login from "pages/authentication/login/Login";
import VerifyEmail from "pages/authentication/verifyEmail";
import VerifyPassword from "pages/authentication/verifyPassword/VerifyPassword";
import VerifySSO from "pages/authentication/verifySSO";
import React, { Suspense } from 'react';

import Forbidden from "pages/errors/forbidden";
import NotFound from "pages/errors/notFound";
// import ManagerBackup from "pages/manager-backup";
// import ManagerHistorys from "pages/manager-history";
import ParticipantStatistics from "pages/manager-meeting/participantStatistics";

import MenuPage from "pages/menu";
import LayoutPackageManagement from "pages/service-package-management/components/LayoutPackageManagement";

import { Loader } from "components/common/Loader";
import { PATH_ROOT } from "constants/api";
import ManagerMeetingsPage from "pages/manager-meeting";
import { CalendarMeetings, MeetingDetails, ScheduleMeeting } from "pages/manager-meeting/components";
import ManagerMeetingsOutlet from "pages/manager-meeting/components/MeetingDetailsOutlet";
import { CreateUpdateVersion, ListVersion } from "pages/manager-version/pages";
import MCUControlPage from "pages/mcu";
import MCUControl from "pages/mcu/MCUControl";
import MCUSettings from "pages/mcu/MCUSettings";
import MCUStatus from "pages/mcu/MCUStatus";
import MonitorRoomPage from "pages/system-monitor/pages/MonitorRoomPage";
import { Navigate, createBrowserRouter } from "react-router-dom";
import { loaderCheckUser, loaderCheckUserAuth, setIsLoaded, setIsLoading } from "utils/helpers";
import PrivateRoutes from "./PrivateRotes";

const lazyRetry = function (componentImport) {

    return new Promise((resolve, reject) => {

        const hasRefreshed = JSON.parse(
            window.sessionStorage.getItem('retry-lazy-refreshed') || 'false'
        );

        componentImport().then((component) => {
            window.sessionStorage.setItem('retry-lazy-refreshed', 'false');
            resolve(component);
        }).catch((error) => {
            if (!hasRefreshed) { // not been refreshed yet
                window.sessionStorage.setItem('retry-lazy-refreshed', 'true');
                return window.location.reload(); // refresh the page
            }
            reject(error);
        });
    });
};

const ManagerRolesPage = React.lazy(() => lazyRetry(() => import("pages/manager-roles/in")))
const ManagerSystems = React.lazy(() => lazyRetry(() => import("pages/manager-systems")))
const ManagerUsers = React.lazy(() => lazyRetry(() => import("pages/manager-users")))
const ManagerUserPage = React.lazy(() => lazyRetry(() => import("pages/manager-users/ManagerUserPage")))
const CreateUser = React.lazy(() => lazyRetry(() => import("pages/manager-users/pages/CreateUser")))
const ManagerUsersSIP = React.lazy(() => lazyRetry(() => import("pages/manager-users-SIP")))
const CreateUserSIP = React.lazy(() => lazyRetry(() => import("pages/manager-users-SIP/CreateUserSIP")))
const ManagerUsersSIPRoutePage = React.lazy(() => lazyRetry(() => import("pages/manager-users-SIP/in")))
const PhoneBookPage = React.lazy(() => lazyRetry(() => import("pages/phonebook")))
const PhonebookLayout = React.lazy(() => lazyRetry(() => import("pages/phonebook/PhonebookLayout")))
const GroupMembersRoute = React.lazy(() => lazyRetry(() => import("pages/phonebook/components/GroupMembersRoute")))
const ManagerRoles = React.lazy(() => lazyRetry(() => import("pages/manager-roles")))
const DetailFunctionPage = React.lazy(() => lazyRetry(() => import("pages/manager-roles/components/DetailFunctionPage")))
const EditRole = React.lazy(() => lazyRetry(() => import("pages/manager-roles/components/EditRole")))
const ManagerContentLandingPage = React.lazy(() => lazyRetry(() => import("pages/content-landingpage")))
const DetailContentLandingpage = React.lazy(() => lazyRetry(() => import("pages/content-landingpage/detail")))
const ContentLandingpage = React.lazy(() => lazyRetry(() => import("pages/content-landingpage/contentLandingpage").then(module => ({ default: module.ContentLandingpage }))))
const Profile = React.lazy(() => lazyRetry(() => import("pages/profile")))
const SetupMultiFactorAuthentication = React.lazy(() => lazyRetry(() => import("pages/profile/pages/SetupMultiFactorAuthentication")))
const StoragePage = React.lazy(() => lazyRetry(() => import("pages/manager-storage")))
const CallSip = React.lazy(() => lazyRetry(() => import("pages/call-sip")))
const ManagerWhiteboard = React.lazy(() => lazyRetry(() => import("pages/manager-whiteboard")))
const ManagerWhiteboardPage = React.lazy(() => lazyRetry(() => import("pages/manager-whiteboard/in")))
const WhiteBoardShareManagement = React.lazy(() => lazyRetry(() => import("../pages/manager-whiteboard/share-board")))
const ManagerRecordings = React.lazy(() => lazyRetry(() => import("pages/manager-recordings")))
const RecordingList = React.lazy(() => lazyRetry(() => import("pages/manager-recordings/pages/RecordingList")))
const SharingRecord = React.lazy(() => lazyRetry(() => import("pages/manager-recordings/pages/SharingRecord")))
const DetailRecord = React.lazy(() => lazyRetry(() => import("pages/manager-recordings/pages/DetailRecord")))
const AccessLogPage = React.lazy(() => lazyRetry(() => import("pages/access-log")))
const HistoryPage = React.lazy(() => lazyRetry(() => import("pages/history")))
const SystemMonitor = React.lazy(() => lazyRetry(() => import("pages/system-monitor")))
const EndpointMonitorDetail = React.lazy(() => lazyRetry(() => import("pages/system-monitor/EndpointMonitorDetail")))
const MonitorDetailRoom = React.lazy(() => lazyRetry(() => import("pages/system-monitor/pages/MonitorDetailRoomPage")))
const ServicePackageManagement = React.lazy(() => lazyRetry(() => import("pages/service-package-management")))
const DetailPackage = React.lazy(() => lazyRetry(() => import("pages/service-package-management/components/DetailPackage")))
const ManagerMember = React.lazy(() => lazyRetry(() => import("pages/manager-member")))
const CreateNewPassword = React.lazy(() => lazyRetry(() => import("pages/authentication/forgotPassword/CreateNewPassword")))
const ChangePasswordFirst = React.lazy(() => lazyRetry(() => import("pages/authentication/changePassword/ChangePasswordFirst")))
const ConfirmPassword = React.lazy(() => lazyRetry(() => import("pages/authentication/forgotPassword/Confirm")))
const ForgotPassword = React.lazy(() => lazyRetry(() => import("pages/authentication/forgotPassword/ForgotPassword")))
const Register = React.lazy(() => lazyRetry(() => import("pages/authentication/register/Register")))
const Subtitles = React.lazy(() => lazyRetry(() => import("pages/subtitles").then(module => ({ default: module.Subtitles }))))
const DetailSubtitles = React.lazy(() => lazyRetry(() => import("pages/subtitles/DetailSubtitles")))
const ManagerSubs = React.lazy(() => lazyRetry(() => import("pages/subtitles/in")))
const ManagerBackup = React.lazy(() => lazyRetry(() => import("pages/manager-backup")))
const ManagerVersion = React.lazy(() => lazyRetry(() => import("pages/manager-version")))
const ControlRoomMCU = React.lazy(() => lazyRetry(() => import("pages/mcu/pages/ControlRoomMCU")))
const SettingUserRole = React.lazy(() => lazyRetry(() => import("pages/manager-users/pages/SettingUserRole")))
const DetailHistoryMFAUser = React.lazy(() => lazyRetry(() => import("pages/history-mfa/components/DetailHistoryMFAUser")))
const LogSumary = React.lazy(() => lazyRetry(() => import("pages/access-log/components/LogSumary")))

const router = createBrowserRouter([
    {
        path: `${PATH_ROOT}/register/verifyEmail`,
        Component: VerifyEmail
    },
    {
        path: `${PATH_ROOT}/register/verifySSO`,
        Component: VerifySSO,
    },
    {
        path: `${PATH_ROOT}/register/verifySSO/:provider`,
        Component: VerifySSO,
    },
    {
        path: `${PATH_ROOT}/iam/callback/:provider`,
        Component: Callback,
    },
    {
        path: `${PATH_ROOT}/auth`,
        Component: Layout,
        loader: loaderCheckUserAuth,
        children: [
            {
                index: true,
                element: <Navigate to="login" />
            },
            {
                path: "login",
                Component: Login,
            },
            {
                path: "register",
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <Register />
                    </Suspense>
                )
            },
            {
                path: "change-password",
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <ChangePasswordFirst />
                    </Suspense>
                )
            },
            {
                path: "forgot-password",
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <ForgotPassword />
                    </Suspense>
                )
            },
            {
                path: "confirm-password",
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <ConfirmPassword />
                    </Suspense>
                )
            },
            {
                path: "reset-password",
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <CreateNewPassword />
                    </Suspense>
                )
            }
        ]
    },
    {
        id: "root",
        path: PATH_ROOT,
        loader: loaderCheckUser,
        Component: Main,
        children: [
            {
                index: true,
                element: <Navigate to="manager-meeting" />
            },
            {
                path: "*",
                Component: NotFound
            },
            {
                path: 'manager-meeting',
                element: <ManagerMeetingsPage />,
                loader: async () => {
                    setIsLoading()
                    setTimeout(() => {
                        setIsLoaded()
                    }, 1500);
                    return null
                },
                handle: {
                    crumb: 'manager-meeting'
                },
                children: [
                    {
                        index: true,
                        Component: CalendarMeetings
                    },
                    {
                        path: 'schedule-meeting/create',
                        element: <ScheduleMeeting />,
                        handle: { crumb: "schedule-meeting/create" }
                    },
                    {
                        path: 'schedule-meeting/update',
                        element: <PrivateRoutes func={FUNCTIONS.MANAGE_MEETING_SCHEDULE_EDIT}>
                            <ScheduleMeeting />
                        </PrivateRoutes>,
                        handle: { crumb: "schedule-meeting/update" }
                    },
                    {
                        path: 'schedule-meeting/copy',
                        element: <PrivateRoutes func={FUNCTIONS.MANAGE_MEETING_SCHEDULE_EDIT}>
                            <ScheduleMeeting />
                        </PrivateRoutes>,
                        handle: { crumb: "schedule-meeting/copy" }
                    },
                    {
                        path: 'details/:roomIdByURL',
                        Component: ManagerMeetingsOutlet,
                        loader: async () => {
                            setIsLoading()
                            setTimeout(() => {
                                setIsLoaded()
                            }, 1500);
                            return null
                        },
                        handle: {
                            crumb: 'details',
                            params: 'roomIdByURL'
                        },
                        children: [
                            {
                                index: true,
                                Component: MeetingDetails
                            },
                            {
                                path: 'statistics',
                                Component: ParticipantStatistics,
                                loader: async () => {
                                    setIsLoading()
                                    setTimeout(() => {
                                        setIsLoaded()
                                    }, 1500);
                                    return null
                                },
                                handle: {
                                    crumb: 'statistics',
                                }
                            }
                        ]
                    }
                ]
            },
            {
                path: "menu",
                Component: MenuPage,
                handle: { crumb: "menu" }
            },
            {
                path: "contacts",
                handle: { crumb: "contacts" },
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <PhonebookLayout />
                    </Suspense>
                ),
                children: [
                    {
                        index: true,
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <PrivateRoutes func={FUNCTIONS.MANAGE_CONTACTS_VIEW}>
                                    <PhoneBookPage />
                                </PrivateRoutes>
                            </Suspense>
                        )
                    },
                    {
                        path: "groups/:groupId",
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <PrivateRoutes func={FUNCTIONS.MANAGE_CONTACTS_VIEW}>
                                    <GroupMembersRoute />
                                </PrivateRoutes>
                            </Suspense>
                        ),
                        handle: { crumb: "groups", params: "groupId" }
                    }
                ]
            },
            {
                path: "manager-backup",
                Component: () => (
                    <PrivateRoutes func={FUNCTIONS.MANAGER_BACKUP_VIEW}>
                        <Suspense fallback={<Loader />}>
                            <ManagerBackup />
                        </Suspense>
                    </PrivateRoutes>
                ),
                handle: { crumb: "manager-backup" }
            },
            // {
            //     path: "organization",
            //     element: (
            //         <Suspense fallback={<Loader />}>
            //             <PhoneBookPage />
            //         </Suspense>
            //     ),
            //     handle: { crumb: "organization" }
            // },
            {
                path: "live-captions",
                element: (
                    <PrivateRoutes func={FUNCTIONS.MANAGE_SUBTITLE_PERSONAL_VIEW}>
                        <Suspense fallback={<Loader />}>
                            <ManagerSubs />
                        </Suspense>
                    </PrivateRoutes>
                ),
                handle: { crumb: "live-captions" },
                children: [
                    {
                        index: true,
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <Subtitles />
                            </Suspense>
                        )
                    },
                    {
                        path: 'detail',
                        element: (
                            <Suspense fallback={<Loader />}>
                                <DetailSubtitles />
                            </Suspense>
                        ),
                        handle: { crumb: "detail" }
                    },
                ]
            },
            {
                path: "manager-diary",
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <PhoneBookPage />
                    </Suspense>
                ),
                handle: { crumb: "manager-diary" }
            },
            {
                path: "manager-sip",
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <PhoneBookPage />
                    </Suspense>
                ),
                handle: { crumb: "manager-sip" }
            },
            {
                path: "license",
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <PhoneBookPage />
                    </Suspense>
                ),
                handle: { crumb: "license" }
            },
            {
                path: "manager-version",
                Component: () => (
                    <PrivateRoutes func={FUNCTIONS.MANAGER_VERSION_VIEW}>
                        <Suspense fallback={<Loader />}>
                            <ManagerVersion />
                        </Suspense>
                    </PrivateRoutes>
                ),
                handle: { crumb: "manager-version" },
                children: [
                    {
                        index: true,
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <ListVersion />
                            </Suspense>
                        )
                    },
                    {
                        path: 'create',
                        Component: () => (
                            <PrivateRoutes func={FUNCTIONS.MANAGER_VERSION_EDIT}>
                                <Suspense fallback={<Loader />}>
                                    <CreateUpdateVersion />
                                </Suspense>
                            </PrivateRoutes>
                        ),
                        handle: { crumb: "manager-version/create" }
                    },
                    {
                        path: 'update/:versionId',
                        Component: () => (
                            <PrivateRoutes func={FUNCTIONS.MANAGER_VERSION_EDIT}>
                                <Suspense fallback={<Loader />}>
                                    <CreateUpdateVersion />
                                </Suspense>
                            </PrivateRoutes>
                        ),
                        handle: { crumb: "manager-version/update" }
                    }
                ]
            },
            {
                path: "manager-users",
                element: (
                    <PrivateRoutes role={GET_ACCOUNT} func={FUNCTIONS.MANAGE_USER_VIEW}>
                        <Suspense fallback={<Loader />}>
                            <ManagerUsers />
                        </Suspense>
                    </PrivateRoutes>
                ),
                handle: { crumb: "manager-users" },
                children: [
                    {
                        index: true,
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <ManagerUserPage />
                            </Suspense>
                        )
                    },
                    {
                        path: 'create',
                        element: (
                            <PrivateRoutes func={FUNCTIONS.MANAGE_USER_EDIT}>
                                <Suspense fallback={<Loader />}>
                                    <CreateUser />
                                </Suspense>
                            </PrivateRoutes>
                        ),
                        handle: { crumb: "manager-users/create" }
                    },
                    {
                        path: "update",
                        element: (
                            <PrivateRoutes func={FUNCTIONS.MANAGE_USER_VIEW}>
                                <Suspense fallback={<Loader />}>
                                    <CreateUser />
                                </Suspense>
                            </PrivateRoutes>
                        ),
                        handle: { crumb: "manager-users/update" }
                    },
                    {
                        path: "view",
                        element: (
                            <PrivateRoutes role={GET_ACCOUNT} func={FUNCTIONS.MANAGE_USER_VIEW}>
                                <Suspense fallback={<Loader />}>
                                    <CreateUser />
                                </Suspense>
                            </PrivateRoutes>
                        ),
                        handle: { crumb: "manager-users/view" }
                    },
                    {
                        path: "settings/:userId",
                        element: (
                            <PrivateRoutes role={EDIT_ACCOUNT} func={FUNCTIONS.MANAGE_USER_VIEW}>
                                <Suspense fallback={<Loader />}>
                                    <SettingUserRole />
                                </Suspense>
                            </PrivateRoutes>
                        ),
                        handle: { crumb: "manager-users/settings" }
                    },
                ]
            },
            {
                path: "manager-user-sip",
                element: (
                    <PrivateRoutes func={FUNCTIONS.MANAGE_SIP_ACCOUNT_VIEW}>
                        <Suspense fallback={<Loader />}>
                            <ManagerUsersSIPRoutePage />
                        </Suspense>
                    </PrivateRoutes>
                ),
                handle: { crumb: "manager-user-sip" },
                children: [
                    {
                        index: true,
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <ManagerUsersSIP />
                            </Suspense>
                        )
                    },
                    {
                        path: 'create',
                        element: (
                            <PrivateRoutes func={FUNCTIONS.MANAGE_SIP_ACCOUNT_EDIT}>
                                <Suspense fallback={<Loader />}>
                                    <CreateUserSIP />
                                </Suspense>
                            </PrivateRoutes>
                        ),
                        handle: { crumb: "manager-user-sip/create" }
                    },
                    {
                        path: "edit/:id",
                        element: (
                            <PrivateRoutes func={FUNCTIONS.MANAGE_SIP_ACCOUNT_VIEW}>
                                <Suspense fallback={<Loader />}>
                                    <CreateUserSIP />
                                </Suspense>
                            </PrivateRoutes>
                        ),
                        handle: { crumb: "manager-user-sip/edit" }
                    },
                ]
            },
            {
                path: "manager-roles",
                element: (
                    <PrivateRoutes isMultipleFuncs={true} func={[FUNCTIONS.MANAGE_ROLE_VIEW, FUNCTIONS.MANAGE_FUNCTION_VIEW]}>
                        <Suspense fallback={<Loader />}>
                            <ManagerRolesPage />
                        </Suspense>
                    </PrivateRoutes>
                ),
                handle: { crumb: "manager-roles" },
                children: [
                    {
                        index: true,
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <ManagerRoles />
                            </Suspense>
                        )
                    },
                    {
                        path: 'manager-function/create',
                        element: (
                            <PrivateRoutes isMultipleFuncs={true} func={[FUNCTIONS.MANAGE_ROLE_EDIT, FUNCTIONS.MANAGE_FUNCTION_EDIT]}>
                                <Suspense fallback={<Loader />}>
                                    <DetailFunctionPage />
                                </Suspense>
                            </PrivateRoutes>
                        ),
                        handle: { crumb: "manager-function/create" }
                    },
                    {
                        path: 'manager-function/update/:id',
                        element: (
                            <PrivateRoutes isMultipleFuncs={true} func={[FUNCTIONS.MANAGE_ROLE_EDIT, FUNCTIONS.MANAGE_FUNCTION_EDIT]}>
                                <Suspense fallback={<Loader />}>
                                    <DetailFunctionPage />
                                </Suspense>
                            </PrivateRoutes>
                        ),
                        handle: { crumb: "manager-function/update" }
                    },
                    {
                        path: 'update/:roleId',
                        element: (
                            <PrivateRoutes isMultipleFuncs={true} func={[FUNCTIONS.MANAGE_ROLE_EDIT, FUNCTIONS.MANAGE_FUNCTION_EDIT]}>
                                <Suspense fallback={<Loader />}>
                                    <EditRole />
                                </Suspense>
                            </PrivateRoutes>
                        ),
                        handle: { crumb: "manager-role-function/update" }
                    }
                ]
            },
            {
                path: "manager-systems",
                element: (
                    <PrivateRoutes isMultipleFuncs={true} func={[FUNCTIONS.MANAGE_SYSTEM_PARAMETER_VIEW, FUNCTIONS.MANAGE_UNIT_POSITION_VIEW, FUNCTIONS.MANAGE_UNIT_VIEW, FUNCTIONS.MANAGER_BRANDING_VIEW]}>
                        <Suspense fallback={<Loader />}>
                            <ManagerSystems />
                        </Suspense>
                    </PrivateRoutes>
                ),
                handle: { crumb: "manager-systems" }
            },
            {
                path: "content-landingpage",
                element: (
                    <PrivateRoutes func={FUNCTIONS.MANAGER_EVENT}>
                        <Suspense fallback={<Loader />}>
                            <ManagerContentLandingPage />
                        </Suspense>
                    </PrivateRoutes>
                ),
                handle: { crumb: "content-landingpage" },
                children: [
                    {
                        index: true,
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <ContentLandingpage />
                            </Suspense>
                        ),
                    },
                    {
                        path: "create",
                        element: (
                            <PrivateRoutes func={FUNCTIONS.MANAGER_EVENT}>
                                <Suspense fallback={<Loader />}>
                                    <DetailContentLandingpage />
                                </Suspense>
                            </PrivateRoutes>
                        ),
                        handle: { crumb: "content-landingpage/create" }
                    },
                    {
                        path: "update/:id",
                        element: (
                            <PrivateRoutes func={FUNCTIONS.MANAGER_EVENT}>
                                <Suspense fallback={<Loader />}>
                                    <DetailContentLandingpage />
                                </Suspense>
                            </PrivateRoutes>

                        ),
                        handle: { crumb: "content-landingpage/update" }
                    },
                ]
            },
            {
                path: "profile-user",
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <Profile />
                    </Suspense>
                ),
                handle: { crumb: "profile-user" }
            },
            {
                path: "setup-multi-factor",
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <SetupMultiFactorAuthentication />
                    </Suspense>
                ),
                handle: { crumb: "profile-user" }
            },
            {
                path: "manager-storage",
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <StoragePage />
                    </Suspense>
                ),
                handle: { crumb: "manager-storage" }
            },
            {
                path: "call-sip",
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <CallSip />
                    </Suspense>
                ),
                handle: { crumb: "" }
            },
            {
                path: "manager-whiteboard",
                element: (
                    <Suspense fallback={<Loader />}>
                        <PrivateRoutes role={GET_ACCOUNT} func={FUNCTIONS.MANAGE_WHITEBOARD_VIEW}>
                            <ManagerWhiteboardPage />
                        </PrivateRoutes>
                    </Suspense>
                ),
                handle: { crumb: "manager-whiteboard" },
                children: [
                    {
                        index: true,
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <ManagerWhiteboard />
                            </Suspense>
                        )
                    },
                    {
                        path: 'share-management/:boardId',
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <WhiteBoardShareManagement />
                            </Suspense>
                        ),
                        handle: {
                            crumb: 'share-management',
                            params: 'boardId'
                        },
                    }
                ]
            },
            {
                path: "manager-recordings",
                element: (
                    <Suspense fallback={<Loader />}>
                        <PrivateRoutes role={GET_ACCOUNT} func={FUNCTIONS.MANAGE_RECORDING_PERSONAL_VIEW}>
                            <ManagerRecordings />
                        </PrivateRoutes>
                    </Suspense>
                ),
                handle: { crumb: "manager-recordings" },
                children: [
                    {
                        index: true,
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <RecordingList />
                            </Suspense>
                        )
                    },
                    {
                        path: 'sharing-record/:recordId',
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <SharingRecord />
                            </Suspense>
                        ),
                        handle: {
                            crumb: "sharing-record",
                            params: 'recordId'
                        }
                    },
                    {
                        path: 'detail-record/:recordRoomId',
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <DetailRecord />
                            </Suspense>
                        ),
                        handle: {
                            crumb: "detail-record",
                            params: 'recordRoomId'
                        }
                    }
                ]
            },
            {
                path: "access-log",
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <AccessLogPage />
                    </Suspense>
                ),
                handle: { crumb: "access-log" },
                children: [
                    {
                        index: true,
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <LogSumary />
                            </Suspense>
                        )
                    },
                    {
                        path: "detail/:accountId",
                        element: (
                            <PrivateRoutes>
                                <Suspense fallback={<Loader />}>
                                    <DetailHistoryMFAUser />
                                </Suspense>
                            </PrivateRoutes>
                        ),
                        handle: { crumb: [{ crumb: 'history-mfa', to: 'access-log' }, 'detail-mfa'] }
                    },
                ]
            },
            {
                path: "history",
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <HistoryPage />
                    </Suspense>
                ),
                handle: { crumb: "history" }
            },
            {
                path: "meeting-monitor",
                element: (
                    <PrivateRoutes func={FUNCTIONS.MANAGE_MEETING_MONITOR_VIEW}>
                        <Suspense fallback={<Loader />}>
                            <SystemMonitor />
                        </Suspense>
                    </PrivateRoutes>
                ),
                handle: { crumb: "meeting-monitor" },
                children: [
                    {
                        index: true,
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <MonitorRoomPage />
                            </Suspense>
                        )
                    },
                    {
                        path: 'detail-monitor/:roomId/:username',
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <EndpointMonitorDetail />
                            </Suspense>
                        ),
                        handle: {
                            crumb: "detail-monitor",
                            params: 'roomId'
                        }
                    },
                    {
                        path: 'detail-monitor/:roomId',
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <MonitorDetailRoom />
                            </Suspense>
                        ),
                        handle: {
                            crumb: "detail-monitor",
                            params: 'roomId'
                        }
                    }
                ]
            },
            {
                path: "mcu-status",
                element: (
                    <PrivateRoutes func={FUNCTIONS.MCU_CONTROL_ROOM_MONITOR}>
                        <Suspense fallback={<Loader />}>
                            <MCUStatus />
                        </Suspense>
                    </PrivateRoutes>
                ),
                handle: { crumb: "mcu-status" },
            },
            {
                path: "mcu-control",
                element: (
                    <PrivateRoutes func={FUNCTIONS.MCU_CONTROL_ROOM_VIEW}>
                        <Suspense fallback={<Loader />}>
                            <MCUControlPage />
                        </Suspense>
                    </PrivateRoutes>
                ),
                handle: { crumb: "mcu-control" },
                children: [
                    {
                        index: true,
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <MCUControl />
                            </Suspense>
                        )
                    },
                    {
                        path: 'control-room/:roomId',
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <ControlRoomMCU />
                            </Suspense>
                        ),
                        handle: {
                            crumb: "control-room",
                            params: 'roomId'
                        }
                    }
                ]
            },
            {
                path: "mcu-settings",
                element: (
                    <PrivateRoutes func={[FUNCTIONS.MCU_CONTROL_SIP_ACCOUNT_VIEW]} isMultipleFuncs={true}>
                        <Suspense fallback={<Loader />}>
                            <MCUSettings />
                        </Suspense>
                    </PrivateRoutes>
                ),
                handle: { crumb: "mcu-settings" },
            },
            {
                path: "service-package-management",
                Component: LayoutPackageManagement,
                handle: { crumb: "service-package-management" },
                children: [
                    {
                        index: true,
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <ServicePackageManagement />
                            </Suspense>
                        )
                    },
                    {
                        path: 'detail-package/:packageId',
                        Component: () => (
                            <Suspense fallback={<Loader />}>
                                <DetailPackage />
                            </Suspense>
                        ),
                        handle: {
                            crumb: "detail-package",
                            params: 'packageId'
                        }
                    }
                ]
            },
            {
                path: "member-management",
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <ManagerMember />
                    </Suspense>
                ),
                handle: { crumb: "member-management" }
            },
            {
                path: "errors/forbidden",
                Component: Forbidden
            },
            {
                path: "error/notfound",
                Component: NotFound
            },
            // {
            //     path: "error/forbidden",
            //     Component: ErrorServer
            // }

        ]
    },
    {
        path: `${PATH_ROOT}/verify`,
        Component: VerifyPassword
    },
    {
        path: `${PATH_ROOT}/download-app`,
        Component: DownloadApp
    },
    {
        path: `${PATH_ROOT}/reset-password`,
        Component: Layout,
        children: [
            {
                index: true,
                Component: () => (
                    <Suspense fallback={<Loader />}>
                        <CreateNewPassword />
                    </Suspense>
                )
            }
        ]
    }, {
        path: `${PATH_ROOT}/home/join-meeting`,
        Component: Layout,
        children: [
            {
                index: true,
                element: < Navigate to="/manager-meeting" replace />
            }
        ]
    }
])

export default router
